import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Row, Col, Alert, Toast, ToastContainer } from 'react-bootstrap';
import { jobService } from '../../services/api/jobService';
import LoadingSpinner from '../common/LoadingSpinner';
import { Job } from '../../types/types';

const JobManagement: React.FC = () => {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Modal states
    const [showModal, setShowModal] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentJob, setCurrentJob] = useState<Job | null>(null);

    // Form states
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [location, setLocation] = useState<string>('');
    const [department, setDepartment] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(true);

    // Toast notification
    const [toast, setToast] = useState<{ show: boolean; message: string; variant: string }>({
        show: false,
        message: '',
        variant: 'success'
    });

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [jobToDelete, setJobToDelete] = useState<Job | null>(null);

    // Load jobs on component mount
    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const response = await jobService.getActiveJobs();
            setJobs(response);
            setError(null);
        } catch (err) {
            setError('Failed to load jobs. Please try again later.');
            console.error('Error fetching jobs:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    const handleOpenAddModal = () => {
        setIsEditing(false);
        setCurrentJob(null);
        resetForm();
        setShowModal(true);
    };

    const handleOpenEditModal = (job: Job) => {
        setIsEditing(true);
        setCurrentJob(job);
        // Populate form with job details
        setTitle(job.title);
        setDescription(job.description);
        setLocation(job.location);
        setDepartment(job.department);
        setIsActive(job.isActive);
        setShowModal(true);
    };

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setLocation('');
        setDepartment('');
        setIsActive(true);
    };

    const showToast = (message: string, variant: string = 'success') => {
        setToast({
            show: true,
            message,
            variant
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const jobData = {
            title,
            description,
            location,
            department,
            isActive,
            ...(isEditing && currentJob ? { id: currentJob.id } : {})
        };

        setLoading(true);
        try {
            if (isEditing && currentJob) {
                await jobService.updateJob(currentJob.id, jobData);
                showToast('Job updated successfully!');
            } else {
                await jobService.createJob(jobData);
                showToast('Job created successfully!');
            }

            fetchJobs();
            handleCloseModal();
        } catch (err) {
            setError(`Failed to ${isEditing ? 'update' : 'create'} job. Please try again.`);
            console.error(`Error ${isEditing ? 'updating' : 'creating'} job:`, err);
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = (job: Job) => {
        setJobToDelete(job);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!jobToDelete) return;

        setLoading(true);
        try {
            await jobService.deleteJob(jobToDelete.id);
            fetchJobs();
            setShowDeleteModal(false);
            setJobToDelete(null);
            showToast('Job deleted successfully!');
        } catch (err) {
            setError('Failed to delete job. Please try again.');
            console.error('Error deleting job:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Card className="shadow-sm">
                <Card.Header className="bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Job Management</h5>
                        <Button variant="primary" onClick={handleOpenAddModal}>
                            <i className="bi bi-plus-circle me-1"></i> Add New Job
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

                    {loading && !showModal && <LoadingSpinner centered />}

                    {!loading && jobs.length === 0 && (
                        <div className="text-center py-5">
                            <p className="text-muted">No jobs found. Click "Add New Job" to create one.</p>
                        </div>
                    )}

                    {!loading && jobs.length > 0 && (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Department</th>
                                        <th>Location</th>
                                        <th>Status</th>
                                        <th>Created Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {jobs.map((job) => (
                                        <tr key={job.id}>
                                            <td>{job.title}</td>
                                            <td>{job.department}</td>
                                            <td>{job.location}</td>
                                            <td>
                                                <span className={`badge ${job.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                    {job.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{new Date(job.createdAt).toLocaleDateString()}</td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleOpenEditModal(job)}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => confirmDelete(job)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>

                {/* Add/Edit Job Modal */}
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{isEditing ? 'Edit Job' : 'Add New Job'}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Job Title*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter job title"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Department*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter department"
                                            value={department}
                                            onChange={(e) => setDepartment(e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Location*</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter job location"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Description*</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="Enter job description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Active"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </>
                                ) : (
                                    'Save Job'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete the job "{jobToDelete?.title}"? This action cannot be undone.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDelete} disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Deleting...
                                </>
                            ) : (
                                'Delete Job'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Card>

            {/* Toast Container - Fixed position at the top right */}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1050 }}>
                <Toast
                    onClose={() => setToast(prev => ({ ...prev, show: false }))}
                    show={toast.show}
                    delay={3000}
                    autohide
                    bg={toast.variant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toast.variant === 'success' ? 'Success' : 'Notification'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toast.variant === 'success' ? 'text-white' : ''}>
                        {toast.message}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default JobManagement;